import React, { useState, useEffect } from 'react';
import styles from './asset/featureFlagToggle.module.scss';
import { getAllFeatureFlags, getCurrentEnvironment } from '../../utils/featureFlags';

const FeatureFlagToggle = ({ isOpen, onClose }) => {
    const [flags, setFlags] = useState({});
    const [overrides, setOverrides] = useState({});

    useEffect(() => {
        if (isOpen) {
            // Load all feature flags
            const allFlags = getAllFeatureFlags();
            setFlags(allFlags);

            // Load overrides from localStorage
            const savedOverrides = localStorage.getItem('featureFlagOverrides');
            if (savedOverrides) {
                try {
                    setOverrides(JSON.parse(savedOverrides));
                } catch (e) {
                    console.error('Failed to parse feature flag overrides', e);
                }
            }
        }
    }, [isOpen]);

    const handleToggle = (featureKey) => {
        const newOverrides = { ...overrides };

        // Toggle the override
        if (newOverrides[featureKey] !== undefined) {
            delete newOverrides[featureKey];
        } else {
            // Set opposite of current state
            newOverrides[featureKey] = !flags[featureKey].isEnabled;
        }

        setOverrides(newOverrides);
        localStorage.setItem('featureFlagOverrides', JSON.stringify(newOverrides));

        // Force reload to apply changes
        window.location.reload();
    };

    const clearOverrides = () => {
        localStorage.removeItem('featureFlagOverrides');
        setOverrides({});
        window.location.reload();
    };

    const getEffectiveState = (featureKey) => {
        if (overrides[featureKey] !== undefined) {
            return overrides[featureKey];
        }
        return flags[featureKey]?.isEnabled || false;
    };

    const hasOverride = (featureKey) => {
        return overrides[featureKey] !== undefined;
    };

    if (!isOpen) return null;

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <h2>Feature Flags</h2>
                    <button className={styles.closeButton} onClick={onClose}>✕</button>
                </div>

                <div className={styles.envInfo}>
                    <span className={styles.envLabel}>Environment:</span>
                    <span className={styles.envValue}>{getCurrentEnvironment()}</span>
                </div>

                <div className={styles.content}>
                    {Object.keys(flags).map((key) => {
                        const flag = flags[key];
                        const isEffectivelyEnabled = getEffectiveState(key);
                        const isOverridden = hasOverride(key);

                        return (
                            <div key={key} className={styles.flagItem}>
                                <div className={styles.flagInfo}>
                                    <div className={styles.flagHeader}>
                                        <h3 className={styles.flagName}>
                                            {flag.name}
                                            {isOverridden && <span className={styles.overrideBadge}>OVERRIDE</span>}
                                        </h3>
                                        <label className={styles.switch}>
                                            <input
                                                type="checkbox"
                                                checked={isEffectivelyEnabled}
                                                onChange={() => handleToggle(key)}
                                            />
                                            <span className={styles.slider}></span>
                                        </label>
                                    </div>
                                    <p className={styles.flagDescription}>{flag.description}</p>
                                    <div className={styles.flagMeta}>
                                        <span className={styles.location}>📍 {flag.location}</span>
                                        <span className={styles.environments}>
                                            🌍 {flag.environments?.join(', ') || 'development only'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className={styles.footer}>
                    <button className={styles.clearButton} onClick={clearOverrides}>
                        Clear All Overrides
                    </button>
                    <p className={styles.note}>
                        💡 Overrides are saved locally and will persist until cleared
                    </p>
                </div>
            </div>
        </div>
    );
};

export default FeatureFlagToggle;
